import { useEffect, useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import { useToast } from '../../hooks/useToast';
import { changePassword } from '../../api/userProfile';
import { getAccessToken, performLogout } from '../../utils/authHelpers';
import {
  INITIAL_PASSWORD,
  PASSWORD_SETUP_EVENT,
  needsPasswordSetup,
  setPasswordSetup,
} from '../../utils/passwordSetup';

/* 초기 비밀번호로 로그인한 계정을 가로막는 온보딩 창.
   닫을 수 없다 — 비밀번호를 정하거나 로그아웃하는 길뿐이다.
   서버도 같은 계정의 다른 API 를 403 으로 막으므로, 화면을 우회해도 아무것도 못 한다. */
const DONE_FLAG = 'passwordSetupDone';

function PasswordOnboardingGate() {
  const { showSuccess, showError } = useToast();
  const [active, setActive] = useState(() => needsPasswordSetup());

  // 새로 읽은 뒤에 «설정했습니다» 안내를 띄운다(아래 handleSubmit 참고)
  useEffect(() => {
    try {
      if (sessionStorage.getItem(DONE_FLAG)) {
        sessionStorage.removeItem(DONE_FLAG);
        showSuccess('비밀번호를 설정했습니다.');
      }
    } catch { /* 저장소를 못 쓰면 안내만 건너뛴다 */ }
    // 화면을 처음 그릴 때 한 번만 — 다시 그릴 때마다 돌면 새로 읽기 전에 표시를 먼저 써 버린다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pw, setPw] = useState({ next: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const sync = () => setActive(needsPasswordSetup());
    window.addEventListener(PASSWORD_SETUP_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PASSWORD_SETUP_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // 토큰이 없으면(로그아웃·만료) 표시도 같이 치운다 — 다음 사람 로그인에 따라붙지 않게.
  useEffect(() => {
    if (active && !getAccessToken()) setPasswordSetup(false);
  }, [active]);

  if (!active || !getAccessToken()) return null;

  const mismatch = pw.confirm && pw.next !== pw.confirm;
  const ready = pw.next.length >= 6 && pw.next === pw.confirm && pw.next !== INITIAL_PASSWORD;

  const handleSubmit = async () => {
    if (!ready || saving) return;
    try {
      setSaving(true);
      await changePassword(INITIAL_PASSWORD, pw.next);
      setPw({ next: '', confirm: '' });
      setPasswordSetup(false);
      /* 창 뒤의 화면은 막혀 있던 동안 이미 불러오기에 실패해 있다(권한·메뉴·목록 모두 403) —
         창만 닫으면 «캠페인 데이터를 불러오는데 실패했습니다»가 남고 새로고침해야 보였다
         (2026-10-05 대표님 발견, 10-06 torriden 고객 문의로 재현). 바꾼 즉시 화면을 새로 읽는다. */
      try { sessionStorage.setItem(DONE_FLAG, '1'); } catch { /* 안내만 못 띄울 뿐 */ }
      window.location.reload();
    } catch (error) {
      const message = error?.response?.data?.message;
      showError(message || '비밀번호를 설정하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    const target = performLogout();
    setPasswordSetup(false);
    window.location.href = target;
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-white p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pw-onboarding-title"
    >
      <div className="w-full max-w-md space-y-5">
        <div>
          <h1 id="pw-onboarding-title" className="text-xl font-bold text-text-dark">
            비밀번호를 직접 설정해 주세요
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            지금 계정은 모두가 아는 초기 비밀번호를 쓰고 있습니다. 본인만 아는 비밀번호로
            바꾸면 바로 사용할 수 있습니다. 설정하기 전에는 다른 화면으로 넘어갈 수 없습니다.
          </p>
        </div>
        <div className="space-y-2.5">
          <Input
            type="password"
            placeholder="새 비밀번호 (6자 이상)"
            value={pw.next}
            autoComplete="new-password"
            onChange={(ev) => setPw((p) => ({ ...p, next: ev.target.value }))}
          />
          <Input
            type="password"
            placeholder="새 비밀번호 확인"
            value={pw.confirm}
            autoComplete="new-password"
            onChange={(ev) => setPw((p) => ({ ...p, confirm: ev.target.value }))}
            onKeyDown={(ev) => { if (ev.key === 'Enter') handleSubmit(); }}
          />
          {mismatch && <p className="text-[11px] text-[#E0806F]">새 비밀번호가 서로 다릅니다.</p>}
          {pw.next === INITIAL_PASSWORD && (
            <p className="text-[11px] text-[#E0806F]">초기 비밀번호와 다른 값을 정해 주세요.</p>
          )}
        </div>
        <div className="flex items-center justify-between">
          <button type="button" className="text-sm text-gray-500 underline" onClick={handleLogout}>
            로그아웃
          </button>
          <Button onClick={handleSubmit} disabled={!ready || saving}>
            {saving ? '저장 중...' : '비밀번호 설정'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PasswordOnboardingGate;
